/**
 * Simplified API client - Axios with 401 refresh interceptor
 * Replaces complex api.js for basic HTTP calls
 */
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import {
  RATE_LIMIT_MAX_RETRIES,
  RATE_LIMIT_STATUS,
  resolveRetryDelayMs,
} from '@/lib/api/rateLimit';
import { handleSessionExpired, refreshToken } from '@/lib/api/tokenRefreshService';
import { newOperationId, traceparentFor } from '@/lib/telemetry';
import { API_CONFIG } from '@/config/api';

// Use shared API_CONFIG for base URL (VITE_API_URL or defaults to localhost:8080)
const url = `${API_CONFIG.baseUrl}${API_CONFIG.apiVersion}/`;

// Extend AxiosRequestConfig to track retry state
interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
  _rlAttempt?: number;
}

const sleep = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

// Axios instance with cookie-based auth (httpOnly e6_session cookie sent automatically)
const api: AxiosInstance = axios.create({
  baseURL: url,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor: give every call an operation id and a matching
// traceparent.
//
// This client is separate from lib/api/client.ts, which has carried both
// headers since the operation-id work landed. So a cluster resumed from the
// cluster list was traceable end to end while the same resume from the query
// editor was not: with no traceparent, Envoy mints its own trace id, and
// compute-api mints its own operation id and stamps that on the QueryService.
// The operator then derives its trace id from the annotation, so its spans
// land in a different trace from Envoy's and compute-api's — the trace looked
// like it simply stopped at the console.
//
// Interceptor rather than per-call arguments because the gap was one endpoint
// (resume) out of everything that goes through this client; the next one added
// would have had the same hole.
//
// Both retry paths below replay through api(originalRequest), which runs this
// interceptor again — so an id already on the config is reused rather than
// replaced. A 401 token refresh or a 429 backoff is still the same user action,
// and re-minting would split it into two unrelated operations, with the failed
// attempt's id in the browser log and the retry's id on the QueryService. The
// traceparent is rebuilt either way, so each attempt gets its own span id
// inside the one trace.
api.interceptors.request.use((config) => {
  const existing = config.headers.get('X-Operation-Id');
  const operationId = typeof existing === 'string' && existing ? existing : newOperationId();
  config.headers.set('X-Operation-Id', operationId);
  // Null when the id is not 32 hex, which happens only on the Web Crypto
  // fallback. Envoy ignores a malformed traceparent silently, which looks
  // identical to sending none, so send none rather than something broken.
  const traceparent = traceparentFor(operationId);
  if (traceparent) {
    config.headers.set('traceparent', traceparent);
  }
  return config;
});

// Response interceptor: on 401, attempt token refresh and retry once.
// On 429, retry up to RATE_LIMIT_MAX_RETRIES times honouring Retry-After
// (or capped exponential backoff when the header is absent).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retried) {
      originalRequest._retried = true;

      const result = await refreshToken();
      if (result.success) {
        // Token refreshed — replay the original request
        return api(originalRequest);
      }
      // Refresh failed — show session expired overlay
      handleSessionExpired(result.logoutUrl);
      return Promise.reject(error);
    }

    if (error.response?.status === RATE_LIMIT_STATUS && originalRequest) {
      const attempt = originalRequest._rlAttempt ?? 0;
      if (attempt < RATE_LIMIT_MAX_RETRIES) {
        originalRequest._rlAttempt = attempt + 1;
        const retryAfter = error.response.headers?.['retry-after'] as string | undefined;
        const delay = resolveRetryDelayMs(retryAfter, attempt);
        await sleep(delay);
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// Simple logout function without complex logic
export const logout = (): void => {
  console.log('Simple logout - clearing storage');
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = '/login';
};

// Stub implementations for compatibility
export const checkAccessTokenExpiry = async (): Promise<boolean> => {
  return true;
};

export const RefreshToken = async (): Promise<boolean> => {
  // Delegate to the centralized refresh service
  const result = await refreshToken();
  return result.success;
};

export default api;
